import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  X, 
  Copy, 
  Download, 
  QrCode, 
  Eye, 
  EyeOff, 
  Lock, 
  Unlock,
  Trash2,
  Calendar,
  Clock,
  DollarSign,
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  ExternalLink,
  Share2
} from 'lucide-react';
import { Ticket, TicketStatus } from '../../types/ticket.types';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 25px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const Header = styled.div`
  padding: 30px 30px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
`;

const TicketTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 10px 0;
  color: #ffd700;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const TicketSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
`;

const Content = styled.div`
  padding: 20px 30px 30px;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #ffd700;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const InfoLabel = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
`;

const InfoValue = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
`;

const StatusBadge = styled.div<{ status: TicketStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  background: ${props => {
    switch (props.status) {
      case 'active': return 'rgba(76, 175, 80, 0.2)';
      case 'used': return 'rgba(244, 67, 54, 0.2)';
      case 'expired': return 'rgba(255, 152, 0, 0.2)';
      case 'cancelled': return 'rgba(158, 158, 158, 0.2)';
      default: return 'rgba(33, 150, 243, 0.2)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'active': return 'rgba(76, 175, 80, 0.5)';
      case 'used': return 'rgba(244, 67, 54, 0.5)';
      case 'expired': return 'rgba(255, 152, 0, 0.5)';
      case 'cancelled': return 'rgba(158, 158, 158, 0.5)';
      default: return 'rgba(33, 150, 243, 0.5)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active': return '#4caf50';
      case 'used': return '#f44336';
      case 'expired': return '#ff9800';
      case 'cancelled': return '#9e9e9e';
      default: return '#2196f3';
    }
  }};
`;

const TicketClassBadge = styled.div<{ class: string }>`
  background: ${props => {
    switch (props.class) {
      case 'first': return 'linear-gradient(45deg, #ffd700, #ffed4e)';
      case 'second': return 'linear-gradient(45deg, #c0c0c0, #e5e5e5)';
      case 'third': return 'linear-gradient(45deg, #cd7f32, #daa520)';
      case 'premium': return 'linear-gradient(45deg, #9c27b0, #e1bee7)';
      case 'vip': return 'linear-gradient(45deg, #ff5722, #ff8a65)';
      default: return 'linear-gradient(45deg, #2196f3, #64b5f6)';
    }
  }};
  color: ${props => props.class === 'second' ? '#333' : 'white'};
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const MetadataSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const MetadataItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const MetadataLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const MetadataValue = styled.span`
  color: white;
  font-weight: 500;
  font-family: monospace;
  font-size: 0.8rem;
  word-break: break-all;
`;

const ActionsSection = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 30px;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' | 'danger' }>`
  background: ${props => {
    switch (props.variant) {
      case 'primary': return 'linear-gradient(45deg, #4caf50, #8bc34a)';
      case 'secondary': return 'rgba(255, 255, 255, 0.1)';
      case 'danger': return 'rgba(244, 67, 54, 0.8)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'primary': return '#4caf50';
      case 'secondary': return 'rgba(255, 255, 255, 0.3)';
      case 'danger': return '#f44336';
      default: return 'rgba(255, 255, 255, 0.3)';
    }
  }};
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(76, 175, 80, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const WarningMessage = styled.div`
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 20px;
  color: #ff9800;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 20px;
  color: #4caf50;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.9rem;
`;

interface TicketDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket | null;
  onTicketAction: (action: string, ticketId: string, data?: any) => void;
}

const TicketDetailsModal: React.FC<TicketDetailsModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onTicketAction
}) => {
  const [showPin, setShowPin] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'warning'; message: string } | null>(null);

  if (!isOpen || !ticket) return null;

  const handleAction = async (action: string, data?: any) => {
    try {
      await onTicketAction(action, ticket.id, data);
      
      if (action === 'copy') {
        setActionMessage({ type: 'success', message: 'Ticket link copied to clipboard!' });
      } else if (action === 'download') {
        setActionMessage({ type: 'success', message: 'Ticket downloaded successfully!' });
      } else if (action === 'cancel') {
        setActionMessage({ type: 'warning', message: 'Ticket cancelled successfully!' });
        setTimeout(() => onClose(), 2000);
      }
      
      // Clear message after 3 seconds
      setTimeout(() => setActionMessage(null), 3000);
    } catch (error) {
      console.error(`Error performing action ${action}:`, error);
    }
  };

  const getStatusIcon = (status: TicketStatus) => {
    switch (status) {
      case 'active': return <CheckCircle size={16} />;
      case 'used': return <XCircle size={16} />;
      case 'expired': return <Clock size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      default: return <Info size={16} />;
    }
  };

  const getStatusText = (status: TicketStatus) => {
    switch (status) {
      case 'active': return 'Active';
      case 'used': return 'Used';
      case 'expired': return 'Expired';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = new Date() > ticket.expiryDate;
  const canCancel = ticket.status === 'active';
  const canSetPin = ticket.status === 'active' && !ticket.pin;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <Header>
          <TicketTitle>
            <Shield size={32} />
            Ticket Details
          </TicketTitle>
          <TicketSubtitle>
            Complete information about your {ticket.ticketClass} class ticket
          </TicketSubtitle>
        </Header>

        <Content>
          {actionMessage && (
            <div style={{ marginBottom: '20px' }}>
              {actionMessage.type === 'success' ? (
                <SuccessMessage>
                  <CheckCircle size={16} />
                  {actionMessage.message}
                </SuccessMessage>
              ) : (
                <WarningMessage>
                  <AlertTriangle size={16} />
                  {actionMessage.message}
                </WarningMessage>
              )}
            </div>
          )}

          <Section>
            <SectionTitle>
              <Info />
              Basic Information
            </SectionTitle>
            
            <InfoGrid>
              <InfoCard>
                <InfoLabel>Ticket Class</InfoLabel>
                <TicketClassBadge class={ticket.ticketClass}>
                  {ticket.ticketClass.toUpperCase()}
                </TicketClassBadge>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Status</InfoLabel>
                <StatusBadge status={ticket.status}>
                  {getStatusIcon(ticket.status)}
                  {getStatusText(ticket.status)}
                </StatusBadge>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Amount</InfoLabel>
                <InfoValue>KES {ticket.amount.toLocaleString()}</InfoValue>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Ticket ID</InfoLabel>
                <InfoValue style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>
                  {ticket.id}
                </InfoValue>
              </InfoCard>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>
              <Calendar />
              Dates & Timeline
            </SectionTitle>
            
            <InfoGrid>
              <InfoCard>
                <InfoLabel>Purchase Date</InfoLabel>
                <InfoValue>{formatDate(ticket.purchaseDate)}</InfoValue>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Expiry Date</InfoLabel>
                <InfoValue style={{ color: isExpired ? '#f44336' : '#4caf50' }}>
                  {formatDate(ticket.expiryDate)}
                </InfoValue>
              </InfoCard>
              
              {ticket.usedDate && (
                <InfoCard>
                  <InfoLabel>Used Date</InfoLabel>
                  <InfoValue>{formatDate(ticket.usedDate)}</InfoValue>
                </InfoCard>
              )}
              
              {ticket.competitionId && (
                <InfoCard>
                  <InfoLabel>Competition ID</InfoLabel>
                  <InfoValue style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>
                    {ticket.competitionId}
                  </InfoValue>
                </InfoCard>
              )}
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>
              <Shield />
              Security & Access
            </SectionTitle>
            
            <InfoGrid>
              <InfoCard>
                <InfoLabel>PIN Status</InfoLabel>
                <InfoValue>
                  {ticket.pin ? (
                    <span style={{ color: '#4caf50' }}>
                      <Lock size={16} style={{ marginRight: '8px' }} />
                      Set
                    </span>
                  ) : (
                    <span style={{ color: '#ff9800' }}>
                      <Unlock size={16} style={{ marginRight: '8px' }} />
                      Not Set
                    </span>
                  )}
                </InfoValue>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Ticket Link</InfoLabel>
                <InfoValue style={{ fontSize: '0.8rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {ticket.ticketLink}
                </InfoValue>
              </InfoCard>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>
              <Info />
              Legal & Validation
            </SectionTitle>
            
            <MetadataSection>
              <MetadataItem>
                <MetadataLabel>LavernAI Name</MetadataLabel>
                <MetadataValue>{ticket.metadata.lavernaiName}</MetadataValue>
              </MetadataItem>
              
              <MetadataItem>
                <MetadataLabel>Amount Staked</MetadataLabel>
                <MetadataValue>KES {ticket.metadata.amountStaked.toLocaleString()}</MetadataValue>
              </MetadataItem>
              
              <MetadataItem>
                <MetadataLabel>Timestamp</MetadataLabel>
                <MetadataValue>{ticket.metadata.timestamp}</MetadataValue>
              </MetadataItem>
              
              <MetadataItem>
                <MetadataLabel>Validation Hash</MetadataLabel>
                <MetadataValue>{ticket.metadata.validationHash}</MetadataValue>
              </MetadataItem>
              
              <MetadataItem>
                <MetadataLabel>Legal Info</MetadataLabel>
                <MetadataValue>{ticket.metadata.legalInfo}</MetadataValue>
              </MetadataItem>
              
              <MetadataItem>
                <MetadataLabel>Terms</MetadataLabel>
                <MetadataValue>{ticket.metadata.terms}</MetadataValue>
              </MetadataItem>
            </MetadataSection>
          </Section>

          <ActionsSection>
            <ActionButton
              variant="primary"
              onClick={() => handleAction('copy')}
              title="Copy ticket link to clipboard"
            >
              <Copy size={16} />
              Copy Link
            </ActionButton>
            
            <ActionButton
              variant="secondary"
              onClick={() => handleAction('download')}
              title="Download ticket data"
            >
              <Download size={16} />
              Download
            </ActionButton>
            
            <ActionButton
              variant="secondary"
              onClick={() => handleAction('qr')}
              title="Generate QR code"
            >
              <QrCode size={16} />
              QR Code
            </ActionButton>
            
            <ActionButton
              variant="secondary"
              onClick={() => handleAction('share')}
              title="Share ticket"
            >
              <Share2 size={16} />
              Share
            </ActionButton>
            
            {canSetPin && (
              <ActionButton
                variant="secondary"
                onClick={() => handleAction('setPin')}
                title="Set PIN for ticket"
              >
                <Lock size={16} />
                Set PIN
              </ActionButton>
            )}
            
            {canCancel && (
              <ActionButton
                variant="danger"
                onClick={() => handleAction('cancel')}
                title="Cancel ticket and get refund"
              >
                <Trash2 size={16} />
                Cancel Ticket
              </ActionButton>
            )}
            
            <ActionButton
              variant="secondary"
              onClick={() => handleAction('openLink')}
              title="Open ticket link in new tab"
            >
              <ExternalLink size={16} />
              Open Link
            </ActionButton>
          </ActionsSection>
        </Content>
      </ModalContent>
    </ModalOverlay>
  );
};

export default TicketDetailsModal;
